import { useState } from "react";

import { useT } from "../../../shared/i18n";
import {
  Button,
  Card,
  controlClassName,
  Dialog,
  Switch,
} from "../../../shared/ui";
import { Group } from "../Group";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import {
  ObservabilityDenied,
  ObservabilityRefused,
  REASON_CODES,
  type ObservabilityApi,
  type ReasonCode,
  type RegistryStatus,
} from "./observabilityApi";

const REMOTE_SETTING = "remote_user_content_enabled";
const ABILITY_BRIEF_SETTING = "remote_ability_brief_enabled";

type Issue = "denied" | "stepUp" | "failed";

const REMOTE_KEY = "remote";
const ABILITY_BRIEF_KEY = "abilityBrief";

function pendingKey(pending: Pending): string {
  return pending.kind === "provider"
    ? `provider:${pending.providerId}`
    : pending.kind;
}

function classify(error: unknown): Issue {
  if (error instanceof ObservabilityRefused) {
    return error.code === "step_up_unavailable" ? "stepUp" : "failed";
  }
  return error instanceof ObservabilityDenied ? "denied" : "failed";
}

type Pending =
  | { kind: "provider"; providerId: string; next: boolean }
  | { kind: "remote"; next: boolean }
  | { kind: "abilityBrief"; next: boolean };

/**
 * Provider enable/disable and the remote-user-content switch. Every change
 * goes through a confirmation dialog with a reason code. The operator API
 * decides the permission (`model_registry.manage`, `.manage_remote`): a
 * refusal replaces the switches with an explicit message. Switches are
 * disabled while a change is in flight.
 */
export function ProviderSwitches({
  api,
  registry,
  onChanged,
}: {
  api: ObservabilityApi;
  registry: RegistryStatus;
  onChanged: () => void;
}) {
  const t = useT();
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState<ReasonCode>(REASON_CODES[0]);
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState<Readonly<Record<string, Issue>>>({});
  const reportExpired = useReportSessionExpired();

  const remoteOn = registry.overrides.some(
    (item) => item.setting === REMOTE_SETTING && item.value,
  );

  const abilityBriefOn =
    registry.remote_ability_brief_enabled ??
    registry.overrides.some(
      (item) => item.setting === ABILITY_BRIEF_SETTING && item.value,
    );

  const close = () => {
    setPending(null);
    setReason(REASON_CODES[0]);
  };

  const confirm = async () => {
    if (pending === null) return;
    const key = pendingKey(pending);
    setBusy(true);
    try {
      if (pending.kind === "provider") {
        await api.setProvider(pending.providerId, pending.next, reason);
      } else if (pending.kind === "abilityBrief") {
        await api.setRemoteAbilityBrief(pending.next, reason);
      } else {
        await api.setRemoteUserContent(pending.next, reason);
      }
      setIssues((current) =>
        Object.fromEntries(Object.entries(current).filter(([k]) => k !== key)),
      );
    } catch (error: unknown) {
      if (isSessionExpired(error)) reportExpired();
      setIssues((current) => ({ ...current, [key]: classify(error) }));
    } finally {
      setBusy(false);
      close();
      // Success or not, the registry is the source of truth.
      onChanged();
    }
  };

  const issueText = (key: string): string | null => {
    const issue = issues[key];
    if (issue === undefined) return null;
    if (issue === "denied") return t("admin.models.switchDenied");
    if (issue === "stepUp") return t("admin.models.stepUpUnavailable");
    return t("admin.models.switchFailed");
  };

  const row = (
    key: string,
    label: string,
    checked: boolean,
    toggle: (next: boolean) => Pending,
    blockedNote?: string,
  ) => {
    const issue = issues[key];
    const text = issueText(key);
    const blocked = issue === "denied" || issue === "stepUp";
    return (
      <div key={key} className="flex flex-col">
        <Switch
          label={label}
          checked={checked}
          disabled={busy || blocked || blockedNote !== undefined}
          onCheckedChange={(next) => {
            setPending(toggle(next));
          }}
        />
        {blockedNote !== undefined && (
          <p className="pb-2.5 text-footnote text-fg-muted">{blockedNote}</p>
        )}
        {text !== null && (
          <p role="alert" className="pb-2.5 text-footnote text-error">
            {text}
          </p>
        )}
      </div>
    );
  };

  const question =
    pending === null
      ? ""
      : pending.kind === "abilityBrief"
        ? pending.next
          ? t("admin.models.confirmAbilityBriefOn")
          : t("admin.models.confirmAbilityBriefOff")
        : pending.kind === "remote"
          ? pending.next
            ? t("admin.models.confirmRemoteOn")
            : t("admin.models.confirmRemoteOff")
          : pending.next
            ? t("admin.models.confirmEnable")
            : t("admin.models.confirmDisable");
  const subject = pending?.kind === "provider" ? pending.providerId : undefined;

  return (
    <Group title={t("admin.models.switches")}>
      <Card flush>
        <div className="flex flex-col divide-y divide-line-subtle px-4">
          {registry.providers.map((provider) =>
            row(
              `provider:${provider.id}`,
              `${t("admin.models.providerSwitch")}: ${provider.id}`,
              provider.effective_enabled,
              (next) => ({
                kind: "provider",
                providerId: provider.id,
                next,
              }),
            ),
          )}
          {row(
            REMOTE_KEY,
            t("admin.models.remoteSwitch"),
            remoteOn,
            (next) => ({ kind: "remote", next }),
            registry.remote_user_content_mutable === false
              ? t("admin.models.remoteImmutable")
              : undefined,
          )}
          {row(
            ABILITY_BRIEF_KEY,
            t("admin.models.abilityBriefSwitch"),
            abilityBriefOn,
            (next) => ({ kind: "abilityBrief", next }),
          )}
          <p className="py-2.5 text-footnote text-fg-muted">
            {t("admin.models.abilityBriefHelp")}
          </p>
        </div>
      </Card>
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !busy) close();
        }}
        title={question}
        description={
          pending?.kind === "abilityBrief"
            ? t("admin.models.abilityBriefHelp")
            : subject
        }
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={close}>
              {t("admin.models.cancel")}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                void confirm();
              }}
            >
              {t("admin.models.confirm")}
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-1 text-caption text-fg-muted">
          {t("admin.models.reason")}
          <select
            className={controlClassName}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value as ReasonCode);
            }}
          >
            {REASON_CODES.map((code) => (
              <option key={code} value={code}>
                {t(`admin.models.reason.${code}`)}
              </option>
            ))}
          </select>
        </label>
      </Dialog>
    </Group>
  );
}
